import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { FileText, Plus, X, Trash2 } from "lucide-react";
import { Tag, ShieldCheck, Stack } from "@phosphor-icons/react";
import { Loading } from "../../components";
import { PageHeader, Button, Input, Textarea, Select, MultiSelect, Checkbox, SeoFields } from "../../components/ui";

/* Client-side image guard — mirrors the API's ImageUpload allowlist (jpg/png/webp/gif, ≤15 MB).
   The server enforces this too; this is for fast UX feedback, not security. */
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const ACCEPT_IMAGE = ALLOWED_IMAGE_TYPES.join(",");
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const imageRejectReason = (file) => {
  if (!ALLOWED_IMAGE_TYPES.includes(file?.type)) return "type"; // not jpg/png/webp/gif
  if (file.size > MAX_IMAGE_BYTES) return "size";
  return null;
};

/* Flatten the nested category tree for the picker. */
const flattenTree = (nodes, depth = 0, acc = []) => {
  (nodes || []).forEach((n) => {
    acc.push({ id: n.id, name: n.name, depth });
    if (n.children?.length) flattenTree(n.children, depth + 1, acc);
  });
  return acc;
};

/* Normalize a product's `details` (array of {key,value} | object map | null) to rows. */
const normalizeDetails = (details) => {
  if (Array.isArray(details)) {
    return details
      .map((d) =>
        d && typeof d === "object" ? { key: d.key ?? "", value: d.value ?? "" } : null
      )
      .filter(Boolean);
  }
  if (details && typeof details === "object") {
    return Object.entries(details).map(([key, value]) => ({ key, value: String(value) }));
  }
  return [];
};

const emptyForm = {
  name: "",
  slug: "",
  meta_title: "",
  meta_description: "",
  description: "",
  category_id: "",
  category_ids: [],
  brand_id: "",
  reference: "",
  type: "",
  price: "",
  compare_price: "",
  stock: "",
  sku: "",
  images: [],
  image_alts: [],
  fiche_technique: null,
  is_top_seller: false,
  is_special_offer: false,
  is_featured: false,
  is_active: true,
};

export default function ProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(emptyForm);
  const [details, setDetails] = useState([]); // [{ key, value }]
  const [options, setOptions] = useState([]); // [{ name, values: [] }]
  const [variants, setVariants] = useState([]); // [{ id?, combo, sku, price, compare_price, stock, image, is_active }]

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [fileChanged, setFileChanged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasVariants = options.some(
    (o) => o.name.trim() && o.values.filter((v) => v.trim()).length
  );

  // Quote-only product: customers request a devis instead of seeing a price.
  const isQuote = form.type === "demander_devis";

  /* -------- load reference data + product -------- */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [catRes, brandRes] = await Promise.all([
          axiosInstance.post("/categories/search", {}),
          axiosInstance.post("/brands/search", {}),
        ]);
        setCategories(catRes.data?.data || []);
        setBrands(brandRes.data?.data || []);

        if (isEdit) {
          const { data: product } = await axiosInstance.get(`/products/get/${id}`);
          hydrate(product);
        }
      } catch (e) {
        toast.error(e.response?.data?.message || "Failed to load product");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const hydrate = (product) => {
    const catIds = (product.categories || []).map((c) => c.id);
    setForm({
      ...emptyForm,
      ...product,
      category_id: product.category_id || "",
      category_ids: catIds.length ? catIds : product.category_id ? [product.category_id] : [],
      brand_id: product.brand_id || "",
      price: product.price ?? "",
      compare_price: product.compare_price ?? "",
      stock: product.stock ?? "",
      images: product.images || [],
      image_alts: product.image_alts || [],
    });
    setDetails(normalizeDetails(product.details));

    const opts = (product.options || []).map((o) => ({
      name: o.name,
      values: (o.values || []).map((v) => v.value),
    }));
    setOptions(opts);

    const optionNames = opts.map((o) => o.name);
    setVariants(
      (product.variants || []).map((v) => {
        const combo = {};
        (v.optionValues || v.option_values || []).forEach((ov) => {
          if (ov.option?.name) combo[ov.option.name] = ov.value;
        });
        return {
          id: v.id,
          combo,
          key: optionNames.map((n) => combo[n]).join(" / "),
          sku: v.sku || "",
          price: v.price ?? "",
          compare_price: v.compare_price ?? "",
          stock: v.stock ?? "",
          image: v.image || null,
          is_active: v.is_active ?? true,
        };
      })
    );
  };

  /* -------- scalar fields -------- */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const handleImageChange = (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = ""; // let the user re-pick the same file after a rejection

    const valid = [];
    picked.forEach((file) => {
      const reason = imageRejectReason(file);
      if (reason === "type") {
        toast.warning(`"${file.name}" ignoré : seules les images JPG, PNG, WEBP ou GIF sont acceptées.`);
      } else if (reason === "size") {
        toast.warning(`"${file.name}" ignoré : dépasse la taille maximale de 15 Mo.`);
      } else {
        valid.push(file);
      }
    });
    if (!valid.length) return;

    let loaded = 0;
    const base64Images = [];
    valid.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        base64Images.push(reader.result);
        loaded += 1;
        if (loaded === valid.length) {
          setForm((prev) => ({
            ...prev,
            images: [...prev.images, ...base64Images],
            image_alts: [...(prev.image_alts || []), ...base64Images.map(() => "")],
          }));
        }
      };
      reader.readAsDataURL(file);
    });
  };
  const removeImage = (index) =>
    setForm((p) => ({
      ...p,
      images: p.images.filter((_, i) => i !== index),
      image_alts: (p.image_alts || []).filter((_, i) => i !== index),
    }));
  const setImageAlt = (index, value) =>
    setForm((p) => {
      const alts = [...(p.image_alts || [])];
      alts[index] = value;
      return { ...p, image_alts: alts };
    });

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => setForm((p) => ({ ...p, fiche_technique: reader.result }));
      setFileChanged(true);
    } else {
      toast.warning("Veuillez sélectionner un fichier PDF valide.");
    }
  };
  const handleRemoveFile = () => {
    setForm((p) => ({ ...p, fiche_technique: null }));
    setFileChanged(false);
  };

  /* -------- details (key/value) -------- */
  const addDetail = () => setDetails((p) => [...p, { key: "", value: "" }]);
  const removeDetail = (i) => setDetails((p) => p.filter((_, idx) => idx !== i));
  const setDetailField = (i, field, value) =>
    setDetails((p) => p.map((d, idx) => (idx === i ? { ...d, [field]: value } : d)));

  /* -------- options -------- */
  const addOption = () => setOptions((p) => [...p, { name: "", values: [] }]);
  const removeOption = (i) => setOptions((p) => p.filter((_, idx) => idx !== i));
  const setOptionName = (i, name) =>
    setOptions((p) => p.map((o, idx) => (idx === i ? { ...o, name } : o)));
  const addOptionValue = (i, value) => {
    const v = value.trim();
    if (!v) return;
    setOptions((p) =>
      p.map((o, idx) =>
        idx === i && !o.values.includes(v) ? { ...o, values: [...o.values, v] } : o
      )
    );
  };
  const removeOptionValue = (i, value) =>
    setOptions((p) =>
      p.map((o, idx) => (idx === i ? { ...o, values: o.values.filter((x) => x !== value) } : o))
    );

  /* -------- variant generation -------- */
  const optionNames = useMemo(
    () => options.map((o) => o.name.trim()).filter(Boolean),
    [options]
  );
  const keyOf = (combo) => optionNames.map((n) => combo[n]).join(" / ");

  const generateVariants = () => {
    const valid = options.filter(
      (o) => o.name.trim() && o.values.filter((v) => v.trim()).length
    );
    if (!valid.length) {
      setVariants([]);
      return;
    }
    let combos = [{}];
    valid.forEach((o) => {
      const next = [];
      o.values
        .filter((v) => v.trim())
        .forEach((v) => combos.forEach((c) => next.push({ ...c, [o.name.trim()]: v.trim() })));
      combos = next;
    });
    const existing = Object.fromEntries(variants.map((v) => [keyOf(v.combo), v]));
    setVariants(
      combos.map((combo) => {
        const k = keyOf(combo);
        return (
          existing[k] || {
            combo,
            key: k,
            sku: "",
            // new variants inherit the product's base price by default
            price: form.price ?? "",
            compare_price: form.compare_price ?? "",
            stock: "",
            image: null,
            is_active: true,
          }
        );
      })
    );
  };

  // copy the product's base price onto every variant
  const applyBasePrice = () =>
    setVariants((vs) => vs.map((v) => ({ ...v, price: form.price ?? "", compare_price: form.compare_price ?? "" })));
  const setVariantField = (i, field, value) =>
    setVariants((p) => p.map((v, idx) => (idx === i ? { ...v, [field]: value } : v)));
  const handleVariantImage = (i, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reason = imageRejectReason(file);
    if (reason) {
      toast.warning(
        reason === "size"
          ? `"${file.name}" ignoré : dépasse la taille maximale de 15 Mo.`
          : `"${file.name}" ignoré : seules les images JPG, PNG, WEBP ou GIF sont acceptées.`
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setVariantField(i, "image", reader.result);
    reader.readAsDataURL(file);
  };

  /* -------- submit -------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanOptions = options
        .map((o) => ({ name: o.name.trim(), values: o.values.map((v) => v.trim()).filter(Boolean) }))
        .filter((o) => o.name && o.values.length);

      const cleanVariants = (cleanOptions.length ? variants : []).map((v) => ({
        id: v.id,
        sku: v.sku || null,
        // internal price is kept for both modes (drives devis totals); only the
        // public compare-at price is dropped for quote-only products.
        price: v.price === "" ? null : v.price,
        compare_price: isQuote ? null : v.compare_price === "" ? null : v.compare_price,
        stock: v.stock === "" ? 0 : Number(v.stock),
        image: v.image || null,
        is_active: v.is_active ?? true,
        options: v.combo,
      }));

      const cleanDetails = details
        .map((d) => ({ key: d.key.trim(), value: d.value.trim() }))
        .filter((d) => d.key || d.value);

      const payload = {
        ...form,
        category_ids: form.category_ids,
        category_id: form.category_ids[0] || null, // primary = first selected
        brand_id: form.brand_id || null,
        price: form.price === "" ? null : form.price,
        compare_price: isQuote ? null : form.compare_price === "" ? null : form.compare_price,
        stock: form.stock === "" ? 0 : Number(form.stock),
        details: cleanDetails,
        options: cleanOptions,
        variants: cleanVariants,
      };

      const response = isEdit
        ? await axiosInstance.put(`/products/update/${id}`, payload)
        : await axiosInstance.post("/products/create", payload);

      if (response.data) {
        toast.success(response.data.message);
        navigate("/products");
      }
    } catch (error) {
      const errs = error.response?.data?.errors;
      const first = errs ? Object.values(errs)[0]?.[0] : null;
      toast.error(first || error.response?.data?.message || "Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const categoryOptions = useMemo(
    () =>
      flattenTree(categories).map((o) => ({
        value: o.id,
        label: `${"— ".repeat(o.depth)}${o.name}`,
      })),
    [categories]
  );
  const brandOptions = brands.map((b) => ({ value: b.id, label: b.name }));

  return (
    <div className="mx-auto w-full max-w-5xl p-6 text-start">
      {loading && <Loading />}

      <PageHeader
        title={isEdit ? "Edit product" : "New product"}
        subtitle={isEdit ? "Update information, options and variants." : "Create a product with optional variants."}
        breadcrumbs={[{ label: "Products", to: "/products" }, { label: isEdit ? "Edit" : "New" }]}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => navigate("/products")}>
              Cancel
            </Button>
            <Button type="submit" form="product-form" loading={saving}>
              {isEdit ? "Save changes" : "Create product"}
            </Button>
          </>
        }
      />

      <form id="product-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Basic info */}
        <section className="rounded-2xl border border-line bg-surface p-5">
          <h2 className="mb-4 text-sm font-semibold text-ink">Basic information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Input label="Product name" name="name" value={form.name} onChange={handleInputChange} required placeholder="e.g. Cordless Drill 18V" />
            <Input label="Reference" name="reference" value={form.reference} onChange={handleInputChange} placeholder="e.g. REF-DRILL-18V" />
            <MultiSelect
              label="Categories"
              icon={Tag}
              searchable
              placeholder="Select categories"
              options={categoryOptions}
              value={form.category_ids}
              onChange={(vals) => setForm((p) => ({ ...p, category_ids: vals }))}
            />
            <Select
              label="Brand"
              icon={ShieldCheck}
              searchable
              clearable
              placeholder="Select a brand"
              options={brandOptions}
              value={form.brand_id || null}
              onChange={(v) => setForm((p) => ({ ...p, brand_id: v || "" }))}
            />
            <Select
              label="Pricing mode"
              icon={Stack}
              placeholder="How is this sold?"
              options={[
                { value: "demander_devis", label: "Request a quote (devis)" },
                { value: "display_price", label: "Show price" },
              ]}
              value={form.type || null}
              onChange={(v) => setForm((p) => ({ ...p, type: v || "" }))}
            />
          </div>
          <div className="mt-5">
            <Textarea label="Description" name="description" value={form.description} onChange={handleInputChange} placeholder="Short product description…" />
          </div>
        </section>

        {/* Pricing & stock */}
        <section className="rounded-2xl border border-line bg-surface p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">{isQuote ? "Internal pricing & stock" : "Pricing & Stock"}</h2>
            {hasVariants && <span className="text-xs text-muted">Stock & SKU are per variant</span>}
          </div>

          {isQuote && (
            <p className="mt-2 rounded-lg border border-accent/20 bg-accent-soft px-3 py-2 text-xs text-accent-strong">
              Quote-only product — this price is internal and used to auto-calculate devis totals. It is not shown to customers.
            </p>
          )}

          <div className={`mt-3 grid grid-cols-1 gap-5 ${isQuote ? "md:grid-cols-3" : "md:grid-cols-4"}`}>
            <Input
              label={hasVariants ? (isQuote ? "Internal base price" : "Base price") : isQuote ? "Internal price" : "Price"}
              type="number"
              step="0.01"
              name="price"
              value={form.price}
              onChange={handleInputChange}
              placeholder="0.00"
              hint={hasVariants ? "Default for new variants" : isQuote ? "Used for devis totals" : undefined}
            />
            {!isQuote && (
              <Input label="Compare price" type="number" step="0.01" name="compare_price" value={form.compare_price} onChange={handleInputChange} placeholder="0.00" />
            )}
            <Input label="Stock" type="number" name="stock" value={form.stock} onChange={handleInputChange} disabled={hasVariants} placeholder="0" className={hasVariants ? "disabled:opacity-60" : undefined} />
            <Input label="SKU" name="sku" value={form.sku} onChange={handleInputChange} disabled={hasVariants} placeholder="SKU-000" className={hasVariants ? "disabled:opacity-60" : undefined} />
          </div>
        </section>

        {/* Media */}
        <section className="rounded-2xl border border-line bg-surface p-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-ink">Upload Images</label>
            <input
              type="file" multiple accept={ACCEPT_IMAGE} onChange={handleImageChange}
              className="mt-2 block w-full text-sm text-ink file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-accent-soft file:text-accent hover:file:bg-[#D3EEF1]"
            />
            {!!form.images.length && (
              <>
                <div className="mt-3 flex flex-wrap gap-3">
                  {form.images.map((img, i) => (
                    <div key={i} className="w-32">
                      <div className="relative">
                        <img src={img || "/placeholder.svg"} alt={form.image_alts?.[i] || `Product ${i + 1}`} className="h-24 w-32 rounded-lg border border-line object-cover" />
                        <button type="button" onClick={() => removeImage(i)} className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-red-600 text-white shadow" aria-label="Remove image">×</button>
                      </div>
                      <input
                        value={form.image_alts?.[i] || ""}
                        onChange={(e) => setImageAlt(i, e.target.value)}
                        placeholder="Alt text"
                        className="mt-1.5 h-8 w-full rounded-md border border-line bg-surface-2 px-2 text-xs text-ink outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                      />
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-muted">Alt text describes each image for search engines &amp; screen readers.</p>
              </>
            )}
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-ink">Upload Fiche Technique (PDF)</label>
            <div className="flex items-center gap-3">
              <input
                type="file" accept="application/pdf" onChange={handlePdfChange}
                className="block w-full text-sm text-ink file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
              />
              {form.fiche_technique && (
                <button type="button" onClick={handleRemoveFile} className="px-3 py-2 rounded-md text-sm bg-red-50 text-red-600 hover:bg-red-100">Remove</button>
              )}
            </div>
            {form.fiche_technique && isValidUrl(form.fiche_technique) && !fileChanged && (
              <a href={form.fiche_technique} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs uppercase bg-red-600 text-white px-4 py-2 rounded-lg shadow hover:bg-red-700">
                <FileText size={16} /> Voir le PDF
              </a>
            )}
          </div>
        </section>

        {/* Details (key/value) */}
        <section className="rounded-2xl border border-line bg-surface p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-ink">Details / Specifications</h2>
              <p className="text-xs text-muted">Key &amp; value pairs, e.g. “Weight” → “2.5 kg”.</p>
            </div>
            <button type="button" onClick={addDetail} className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium bg-accent-soft text-accent hover:bg-accent-soft">
              <Plus size={16} /> Add row
            </button>
          </div>
          {details.length === 0 && <p className="text-sm text-faint">No specifications yet.</p>}
          <div className="space-y-2">
            {details.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <input value={d.key} onChange={(e) => setDetailField(i, "key", e.target.value)} placeholder="Key (e.g. Material)"
                  className="h-10 flex-1 rounded-lg bg-surface-2 border border-line px-3 outline-none focus:ring-2 focus:ring-accent" />
                <input value={d.value} onChange={(e) => setDetailField(i, "value", e.target.value)} placeholder="Value (e.g. Stainless steel)"
                  className="h-10 flex-1 rounded-lg bg-surface-2 border border-line px-3 outline-none focus:ring-2 focus:ring-accent" />
                <button type="button" onClick={() => removeDetail(i)} className="p-2 rounded-md text-red-600 hover:bg-red-50" title="Remove">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* Options */}
        <section className="rounded-2xl border border-line bg-surface p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-ink">Options</h2>
              <p className="text-xs text-muted">e.g. Size (S, M, L) or Color (Red, Blue). Leave empty for a simple product.</p>
            </div>
            <button type="button" onClick={addOption} className="inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium bg-accent-soft text-accent hover:bg-accent-soft">
              <Plus size={16} /> Add option
            </button>
          </div>

          {options.map((opt, i) => (
            <div key={i} className="mb-3 rounded-lg border border-line p-3">
              <div className="flex items-center gap-2">
                <input value={opt.name} onChange={(e) => setOptionName(i, e.target.value)} placeholder="Option name (e.g. Size)"
                  className="h-10 flex-1 rounded-lg bg-surface-2 border border-line px-3 outline-none focus:ring-2 focus:ring-accent" />
                <button type="button" onClick={() => removeOption(i)} className="p-2 rounded-md text-red-600 hover:bg-red-50" title="Remove option">
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {opt.values.map((v) => (
                  <span key={v} className="inline-flex items-center gap-1 rounded-full bg-surface-2 border border-line px-3 py-1 text-sm">
                    {v}
                    <button type="button" onClick={() => removeOptionValue(i, v)} className="rounded-full p-0.5 hover:bg-neutral-200">
                      <X size={12} />
                    </button>
                  </span>
                ))}
                <input
                  placeholder="Add value + Enter"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addOptionValue(i, e.target.value);
                      e.target.value = "";
                    }
                  }}
                  className="h-9 w-40 rounded-lg bg-surface border border-line px-3 text-sm outline-none focus:ring-2 focus:ring-accent"
                />
              </div>
            </div>
          ))}

          {hasVariants && (
            <button type="button" onClick={generateVariants} className="mt-1 inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium border border-accent text-accent hover:bg-accent-soft">
              Generate variants
            </button>
          )}
        </section>

        {/* Variants */}
        {hasVariants && variants.length > 0 && (
          <section className="rounded-2xl border border-line bg-surface p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-ink">
                Variants <span className="text-xs font-normal text-muted">({variants.length})</span>
              </h2>
              <button
                type="button"
                onClick={applyBasePrice}
                className="rounded-lg border border-line px-2.5 py-1.5 text-xs font-medium text-muted transition hover:bg-surface-2 hover:text-ink"
              >
                Copy base price to all
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted">
                    <th className="py-2 pr-3">Variant</th>
                    <th className="py-2 pr-3">SKU</th>
                    <th className="py-2 pr-3">{isQuote ? "Int. price" : "Price"}</th>
                    {!isQuote && <th className="py-2 pr-3">Compare</th>}
                    <th className="py-2 pr-3">Stock</th>
                    <th className="py-2 pr-3">Image</th>
                    <th className="py-2 pr-3">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {variants.map((v, i) => (
                    <tr key={v.key || i} className="border-t border-line">
                      <td className="py-2 pr-3 font-medium text-ink whitespace-nowrap">
                        {optionNames.map((n) => v.combo[n]).join(" / ")}
                      </td>
                      <td className="py-2 pr-3">
                        <input value={v.sku} onChange={(e) => setVariantField(i, "sku", e.target.value)} className="h-9 w-28 rounded-md bg-surface-2 border border-line px-2 outline-none focus:ring-2 focus:ring-accent" />
                      </td>
                      <td className="py-2 pr-3">
                        <input type="number" step="0.01" value={v.price} onChange={(e) => setVariantField(i, "price", e.target.value)} className="h-9 w-24 rounded-md bg-surface-2 border border-line px-2 outline-none focus:ring-2 focus:ring-accent" />
                      </td>
                      {!isQuote && (
                        <td className="py-2 pr-3">
                          <input type="number" step="0.01" value={v.compare_price} onChange={(e) => setVariantField(i, "compare_price", e.target.value)} className="h-9 w-24 rounded-md bg-surface-2 border border-line px-2 outline-none focus:ring-2 focus:ring-accent" />
                        </td>
                      )}
                      <td className="py-2 pr-3">
                        <input type="number" value={v.stock} onChange={(e) => setVariantField(i, "stock", e.target.value)} className="h-9 w-20 rounded-md bg-surface-2 border border-line px-2 outline-none focus:ring-2 focus:ring-accent" />
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          {v.image && <img src={v.image} alt="" className="h-9 w-9 rounded object-cover border" />}
                          <label className="cursor-pointer text-xs text-accent hover:underline">
                            {v.image ? "Change" : "Add"}
                            <input type="file" accept={ACCEPT_IMAGE} className="hidden" onChange={(e) => handleVariantImage(i, e)} />
                          </label>
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <Checkbox checked={!!v.is_active} onChange={(val) => setVariantField(i, "is_active", val)} size={16} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Flags */}
        <section className="rounded-2xl border border-line bg-surface p-5">
          <h2 className="text-sm font-semibold text-ink mb-3">Visibility</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { key: "is_top_seller", label: "Top Seller" },
              { key: "is_special_offer", label: "Special Offer" },
              { key: "is_featured", label: "Featured" },
              { key: "is_active", label: "Active" },
            ].map(({ key, label }) => (
              <Checkbox
                key={key}
                label={label}
                checked={!!form[key]}
                onChange={(val) => setForm((p) => ({ ...p, [key]: val }))}
              />
            ))}
          </div>
        </section>

        {/* SEO */}
        <SeoFields
          value={{ slug: form.slug, meta_title: form.meta_title, meta_description: form.meta_description }}
          onChange={(field, val) => setForm((p) => ({ ...p, [field]: val }))}
          fallbackTitle={form.name}
          fallbackDescription={form.description}
          basePath="/products/"
        />

        {/* Footer actions */}
        <div className="flex justify-end gap-2 pb-4">
          <Button type="button" variant="outline" onClick={() => navigate("/products")}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {isEdit ? "Save changes" : "Create product"}
          </Button>
        </div>
      </form>
    </div>
  );
}
